import { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tabs,
  Tab,
  InputAdornment,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FilterAlt,
  Search,
  Download,
  CalendarToday,
  Visibility,
  Edit,
  Delete,
  ArrowUpward,
  ArrowDownward,
  AccountBalance,
  Person,
} from '@mui/icons-material';
import TransactionFilters from './TransactionFilters';

// Altura mínima para filas táctiles
const TOUCH_ROW_HEIGHT = 56;

const POSTransactions = ({ transactions = [] }) => {
  const [tabValue, setTabValue] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    tipoPago: [],
    tipoTransaccion: [],
    empleados: [],
    clientes: [],
    proveedores: [],
    concepto: '',
    fechaInicio: '',
    fechaFin: '',
  });

  // Filtros de la barra principal
  const [periodo, setPeriodo] = useState('diario');
  const [searchTerm, setSearchTerm] = useState('');

  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Filtrar transacciones según tabs y filtros
  const filteredTransactions = useMemo(() => {
    let result = [...transactions];

    // Filtrar por tab
    if (tabValue === 0) {
      result = result.filter((t) => t.tipo === 'venta' || t.tipo === 'cobro');
    } else if (tabValue === 1) {
      result = result.filter((t) => t.tipo === 'gasto' || t.tipo === 'pago');
    } else if (tabValue === 2) {
      result = result.filter((t) => t.tipo === 'cobro');
    } else if (tabValue === 3) {
      result = result.filter((t) => t.tipo === 'pago');
    }

    // Filtrar por tipo de transacción
    if (filters.tipoTransaccion && filters.tipoTransaccion.length > 0) {
      result = result.filter((t) => filters.tipoTransaccion.includes(t.tipo));
    }

    // Filtrar por tipo de pago
    if (filters.tipoPago && filters.tipoPago.length > 0) {
      result = result.filter((t) => {
        const medioPagoLower = t.medioPago.toLowerCase();
        return filters.tipoPago.some((p) => medioPagoLower.includes(p.replace('_', ' ')));
      });
    }

    // Filtrar por empleado
    if (filters.empleados && filters.empleados.length > 0) {
      result = result.filter((t) => {
        const empleadoLower = t.empleado.toLowerCase();
        return filters.empleados.some((e) => empleadoLower.includes(e));
      });
    }

    // Filtrar por cliente
    if (filters.clientes && filters.clientes.length > 0) {
      result = result.filter((t) => t.cliente && filters.clientes.some((c) => t.cliente.toLowerCase().includes(c)));
    }

    // Filtrar por proveedor
    if (filters.proveedores && filters.proveedores.length > 0) {
      result = result.filter((t) => t.proveedor && filters.proveedores.some((p) => t.proveedor.toLowerCase().includes(p)));
    }

    // Filtrar por concepto
    if (filters.concepto) {
      result = result.filter((t) => t.concepto.toLowerCase().includes(filters.concepto.toLowerCase()));
    }

    // Filtrar por rango de fechas
    if (filters.fechaInicio) {
      result = result.filter((t) => t.fecha >= filters.fechaInicio);
    }
    if (filters.fechaFin) {
      result = result.filter((t) => t.fecha <= filters.fechaFin);
    }

    // Filtrar por búsqueda
    if (searchTerm) {
      result = result.filter(
        (t) =>
          t.concepto.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (t.cliente && t.cliente.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (t.proveedor && t.proveedor.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return result;
  }, [tabValue, filters, searchTerm]);

  // Calcular totales
  const totals = useMemo(() => {
    return filteredTransactions.reduce(
      (acc, t) => {
        if (t.tipo === 'venta' || t.tipo === 'cobro') {
          acc.ingresos += t.valor;
        } else {
          acc.egresos += t.valor;
        }
        return acc;
      },
      { ingresos: 0, egresos: 0 }
    );
  }, [filteredTransactions]);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      tipoPago: [],
      tipoTransaccion: [],
      empleados: [],
      clientes: [],
      proveedores: [],
      concepto: '',
      fechaInicio: '',
      fechaFin: '',
    });
  };

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'venta':
        return 'success';
      case 'cobro':
        return 'info';
      case 'gasto':
        return 'warning';
      case 'pago':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'venta':
        return 'Venta';
      case 'cobro':
        return 'Cobro';
      case 'gasto':
        return 'Gasto';
      case 'pago':
        return 'Pago';
      default:
        return tipo;
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'completada':
        return 'success';
      case 'pendiente':
        return 'warning';
      case 'cancelada':
        return 'error';
      default:
        return 'default';
    }
  };

  const hasActiveFilters = Object.values(filters).some(
    (value) => (Array.isArray(value) ? value.length > 0 : value)
  );

  // Columnas a mostrar en móvil
  const visibleColumns = isMobile ? ['fecha', 'tipo', 'concepto', 'valor', 'estado'] : 
                         isTablet ? ['fecha', 'tipo', 'concepto', 'valor', 'medioPago', 'estado'] :
                         ['fecha', 'tipo', 'concepto', 'valor', 'medioPago', 'cliente', 'empleado', 'estado'];

  return (
    <Box>
      {/* Barra de filtros superior */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          {/* Botón de filtros - touch grande */}
          <Button
            variant={hasActiveFilters ? 'contained' : 'outlined'}
            startIcon={<FilterAlt />}
            onClick={() => setFiltersOpen(true)}
            color={hasActiveFilters ? 'primary' : 'inherit'}
            sx={{
              minHeight: 48,
              minWidth: isMobile ? 48 : 'auto',
              px: isMobile ? 1 : 2,
            }}
          >
            {isMobile ? <FilterAlt /> : `Filtros ${hasActiveFilters ? `(${Object.values(filters).flat().filter(Boolean).length})` : ''}`}
          </Button>

          {/* Selector de período */}
          <FormControl size="small" sx={{ minWidth: isMobile ? 90 : 120 }}>
            <InputLabel>Período</InputLabel>
            <Select value={periodo} onChange={(e) => setPeriodo(e.target.value)} label="Período">
              <MenuItem value="diario">Diario</MenuItem>
              <MenuItem value="semanal">Semanal</MenuItem>
              <MenuItem value="mensual">Mensual</MenuItem>
              <MenuItem value="anual">Anual</MenuItem>
              <MenuItem value="personalizado">Personalizado</MenuItem>
            </Select>
          </FormControl>

          {/* Selector de fecha */}
          <TextField
            type="date"
            size="small"
            label="Fecha"
            value="2026-03-05"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: isMobile ? 120 : 150 }}
          />

          {/* Buscador */}
          <TextField
            size="small"
            placeholder={isMobile ? "Buscar..." : "Buscar concepto..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 150 : 200, flexGrow: 1 }}
          />

          {/* Botón de descarga */}
          {!isMobile && (
            <Button variant="outlined" startIcon={<Download />}>
              Descargar
            </Button>
          )}
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={isMobile ? "Ing" : "Ingresos"} icon={<ArrowUpward />} iconPosition="start" />
          <Tab label={isMobile ? "Egr" : "Egresos"} icon={<ArrowDownward />} iconPosition="start" />
          <Tab label={isMobile ? "CXC" : "CXC"} icon={<Person />} iconPosition="start" />
          <Tab label={isMobile ? "CXP" : "CXP"} icon={<AccountBalance />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Totales - responsive */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: '1 1 auto', minWidth: { xs: '100%', sm: 200 } }}>
          <Typography variant="subtitle1" color="text.secondary">
            Ingresos
          </Typography>
          <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold' }}>
            ${totals.ingresos.toFixed(2)}
          </Typography>
        </Paper>
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: '1 1 auto', minWidth: { xs: '45%', sm: 200 } }}>
          <Typography variant="subtitle1" color="text.secondary">
            Egresos
          </Typography>
          <Typography variant="h5" color="error.main" sx={{ fontWeight: 'bold' }}>
            ${totals.egresos.toFixed(2)}
          </Typography>
        </Paper>
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: '1 1 auto', minWidth: { xs: '45%', sm: 200 } }}>
          <Typography variant="subtitle1" color="text.secondary">
            Diferencia
          </Typography>
          <Typography
            variant="h5"
            color={(totals.ingresos - totals.egresos) >= 0 ? 'success.main' : 'error.main'}
            sx={{ fontWeight: 'bold' }}
          >
            ${(totals.ingresos - totals.egresos).toFixed(2)}
          </Typography>
        </Paper>
      </Box>

      {/* Grid de transacciones */}
      <TableContainer 
        component={Paper}
        sx={{ 
          // Scroll horizontal en móvil
          overflowX: 'auto',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.100' }}>
              {visibleColumns.includes('fecha') && <TableCell sx={{ fontWeight: 'bold' }}>Fecha/Hora</TableCell>}
              {visibleColumns.includes('tipo') && <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>}
              {visibleColumns.includes('concepto') && <TableCell sx={{ fontWeight: 'bold' }}>Concepto</TableCell>}
              {visibleColumns.includes('valor') && <TableCell sx={{ fontWeight: 'bold' }} align="right">Valor</TableCell>}
              {visibleColumns.includes('medioPago') && <TableCell sx={{ fontWeight: 'bold' }}>Medio de Pago</TableCell>}
              {visibleColumns.includes('cliente') && <TableCell sx={{ fontWeight: 'bold' }}>Cliente/Proveedor</TableCell>}
              {visibleColumns.includes('empleado') && <TableCell sx={{ fontWeight: 'bold' }}>Empleado</TableCell>}
              {visibleColumns.includes('estado') && <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>}
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No se encontraron transacciones
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTransactions.map((transaction) => (
                <TableRow 
                  key={transaction.id}
                  sx={{ 
                    // Eliminar hover para touch - usar active en su lugar
                    cursor: 'pointer',
                    // Altura táctil mínima
                    minHeight: TOUCH_ROW_HEIGHT,
                    '&:active': {
                      backgroundColor: 'action.selected',
                    },
                    // Transición suave
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {visibleColumns.includes('fecha') && (
                    <TableCell>
                      <Typography variant="body2">{transaction.fecha}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {transaction.hora}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('tipo') && (
                    <TableCell>
                      <Chip
                        label={getTipoLabel(transaction.tipo)}
                        color={getTipoColor(transaction.tipo)}
                        size="small"
                      />
                    </TableCell>
                  )}
                  {visibleColumns.includes('concepto') && (
                    <TableCell>
                      <Typography variant="body2">{transaction.concepto}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {transaction.id}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('valor') && (
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 'bold',
                          color: transaction.tipo === 'venta' || transaction.tipo === 'cobro' ? 'success.main' : 'error.main',
                        }}
                      >
                        ${transaction.valor.toFixed(2)}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('medioPago') && (
                    <TableCell>
                      <Typography variant="body2">{transaction.medioPago}</Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('cliente') && (
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.cliente || transaction.proveedor || '-'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('empleado') && (
                    <TableCell>
                      <Typography variant="body2">{transaction.empleado}</Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('estado') && (
                    <TableCell>
                      <Chip
                        label={transaction.estado}
                        color={getEstadoColor(transaction.estado)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  )}
                  <TableCell align="center">
                    {/* Botones de acción táctiles grandes */}
                    <Tooltip title="Ver">
                      <IconButton 
                        size="medium"
                        sx={{ 
                          minWidth: 48, 
                          minHeight: 48,
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton 
                        size="medium"
                        sx={{ 
                          minWidth: 48, 
                          minHeight: 48,
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton 
                        size="medium"
                        color="error"
                        sx={{ 
                          minWidth: 48, 
                          minHeight: 48,
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de filtros */}
      <TransactionFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filters}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />
    </Box>
  );
};

export default POSTransactions;
